/**
 * useAuth Hook - 管理全局登录状态
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';

interface User {
  id: number;
  username: string;
  display_name: string;
  role: string;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const navigate = useNavigate();

  // 初始化：从 localStorage 加载用户信息
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse user from localStorage:', e);
      }
    }
    setLoading(false);
  }, []);

  // 监听 401 未授权事件
  useEffect(() => {
    const handleUnauthorized = () => {
      setShowLoginDialog(true);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  // 监听用户信息更新事件
  useEffect(() => {
    const handleUserUpdated = () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
          console.log('🔄 用户信息已更新');
        } catch (e) {
          console.error('Failed to parse updated user from localStorage:', e);
        }
      }
    };

    window.addEventListener('user:updated', handleUserUpdated);
    return () => window.removeEventListener('user:updated', handleUserUpdated);
  }, []);

  // 登出
  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // 清除本地存储
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      setUser(null);
      
      // 跳转到项目根链接
      navigate('/');
    }
  };

  // 检查是否已登录
  const isAuthenticated = !!user;

  // 登录弹框成功后的回调
  const handleLoginSuccess = () => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
      // 触发登录成功事件，通知其他组件重新加载数据
      window.dispatchEvent(new CustomEvent('auth:login-success'));
    }
  };

  return {
    user,
    loading,
    isAuthenticated,
    logout,
    showLoginDialog,
    setShowLoginDialog,
    handleLoginSuccess,
  };
};
